import type { Popover as PopoverType } from '@chakra-ui/react'
import { Group, Popover, Portal, Stack } from '@chakra-ui/react'
import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { NativeSelectField, NativeSelectRoot } from '@app/components/ui/native-select'
import { PopoverCloseTrigger } from '@app/components/ui/popover'
import useGetObjects from '@app/hooks/useGetObjects'
import { useListFilterContext } from '@app/pages/shared/listFilterContext'
import type { DomainStrategy } from '@app/types'
import { useStrategiesBulkReassignBusinessContextMutation } from '@graphql/queries'

type DomainObjects = DomainStrategy[]
type Props = PropsWithChildren<Omit<PopoverType.RootProps, 'children'> & { domainObjects: DomainObjects }>

const BulkReassignBusinessContextPopover: FC<Props> = ({ domainObjects, children, ...rest }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const { setRefreshToken } = useListFilterContext()
  const [, bulkReassignBusinessContext] = useStrategiesBulkReassignBusinessContextMutation()

  // This component assumes that the businessContexts have or will be loaded by another component.
  const businessContexts = useGetObjects('businessContext')

  const currentFolderId = domainObjects[0]?.businessContextId || null
  const sortedBusinessContexts = businessContexts
    .filter((folder) => {
      // If the items are in the root folder, remove the root folder from the list.
      if (!currentFolderId && !folder.parentBusinessContextId) {
        return false
      }

      // Filter out the current folder from the list of folders.
      return folder.id !== currentFolderId
    })
    .sort((a, b) => a.name.localeCompare(b.name))

  const [businessContextId, setBusinessContextId] = useState<string | null>(sortedBusinessContexts[0]?.id || null)

  const updateFolders = async (e) => {
    e.preventDefault()

    setLoading(true)

    bulkReassignBusinessContext({
      input: { strategyIds: domainObjects.map((strategy) => strategy.id), businessContextId }
    })
      .then(() => {
        // Use the context refresh token to trigger a re-fetch of the data, this is a workaround until we can
        // get rid of the Maps/Context Graph split and simplify the data fetching logic.
        setRefreshToken(new Date().getTime())
      })
      .finally(() => {
        setLoading(false)
        setOpen(false)
        setBusinessContextId(null)
      })
  }

  return (
    <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)} lazyMount {...rest} closeOnInteractOutside={false}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content>
            <Popover.Arrow />
            <PopoverCloseTrigger />
            <Popover.Header>
              <Popover.Title>Update folder</Popover.Title>
            </Popover.Header>
            <form onSubmit={updateFolders}>
              <Popover.Body>
                <Stack gap={2}>
                  <Field label="New folder" required>
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={businessContextId || ''}
                        onChange={(e) => setBusinessContextId(e.target.value)}
                      >
                        {sortedBusinessContexts.map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.name}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  </Field>
                </Stack>
              </Popover.Body>
              <Popover.Footer justifyContent="flex-end">
                <Group justifyContent="end">
                  <Button onClick={() => setOpen(false)} variant="outline">
                    Cancel
                  </Button>
                  <Button type="submit" loading={loading}>
                    Update
                  </Button>
                </Group>
              </Popover.Footer>
            </form>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

export default BulkReassignBusinessContextPopover
